import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';

const LOGO_URL = "/heft-logo.png";

const Header = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [setIsScrolled]);

  // Close menu and scroll to top when route changes
  useEffect(() => {
    setIsMenuOpen(false);
    // Small delay to let menu close, then scroll to absolute top
    setTimeout(() => {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    }, 50);
  }, [location.pathname, setIsMenuOpen]);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/about', label: 'About' },
    { path: '/services', label: 'Coaching' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/results', label: 'Results' },
    { path: '/resources', label: 'Resources' },
  ];

  const isActive = (path) => location.pathname === path;
  const isHome = location.pathname === '/';

  return (
    <header className={`fixed top-0 w-full z-50 transition-all duration-300 ${
      isScrolled || !isHome
        ? 'bg-white shadow-md' 
        : 'bg-transparent'
    }`}>
      <nav className="container">
        <div className="flex items-center justify-between h-20 md:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center">
            <img 
              src={LOGO_URL} 
              alt="Heft Training" 
              className={`h-12 md:h-16 w-auto transition-all duration-300 ${
                isScrolled || !isHome ? '' : 'invert'
              }`}
            />
          </Link>

          {/* Desktop Navigation - Centered */}
          <div className="hidden lg:flex items-center gap-12">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`relative text-sm font-medium tracking-wider uppercase transition-colors duration-300 py-2 ${
                  isScrolled || !isHome
                    ? isActive(link.path) 
                      ? 'text-[#1A1A1A]' 
                      : 'text-[#6B6B6B] hover:text-[#1A1A1A]'
                    : isActive(link.path) 
                      ? 'text-white' 
                      : 'text-white/70 hover:text-white'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className={`absolute bottom-0 left-0 w-full h-0.5 ${
                    isScrolled || !isHome ? 'bg-[#C9A962]' : 'bg-[#C9A962]'
                  }`} />
                )}
              </Link>
            ))}
          </div>

          {/* Desktop CTA */}
          <Link
            to="/contact"
            className={`hidden lg:flex items-center justify-center px-8 py-3 text-sm font-semibold tracking-wider uppercase transition-all duration-300 ${
              isScrolled || !isHome
                ? 'bg-[#1A1A1A] text-white hover:bg-[#333]'
                : 'bg-[#C9A962] text-[#1A1A1A] hover:bg-[#d4b872]'
            }`}
          >
            Book Now
          </Link>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className={`lg:hidden p-2 transition-colors duration-300 ${
              isScrolled || !isHome ? 'text-[#1A1A1A]' : 'text-white'
            }`}
            aria-label="Toggle menu"
          >
            {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>
      </nav>

      {/* Mobile Navigation - Full Screen Overlay */}
      <div className={`lg:hidden fixed inset-0 bg-white z-40 transition-transform duration-300 ${
        isMenuOpen ? 'translate-x-0' : 'translate-x-full'
      }`}>
        <div className="container h-full flex flex-col">
          {/* Mobile Header */}
          <div className="flex items-center justify-between h-20">
            <img 
              src={LOGO_URL} 
              alt="Heft Training" 
              className="h-12 w-auto"
            />
            <button
              onClick={() => setIsMenuOpen(false)}
              className="p-2 text-[#1A1A1A]"
              aria-label="Close menu"
            >
              <X size={28} />
            </button>
          </div>

          {/* Mobile Nav Links */}
          <div className="flex-1 flex flex-col justify-center pb-20">
            {navLinks.map((link, index) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-3xl font-medium tracking-wider uppercase py-4 border-b border-[#E5E0DB] transition-colors duration-300 ${
                  isActive(link.path) 
                    ? 'text-[#C9A962]' 
                    : 'text-[#1A1A1A] hover:text-[#C9A962]'
                }`}
                style={{fontFamily: "'Oswald', sans-serif"}}
              >
                {link.label}
              </Link>
            ))}
            <Link
              to="/contact"
              className="mt-8 bg-[#1A1A1A] text-white text-center py-4 text-lg font-semibold tracking-wider uppercase"
            >
              Book Free Consultation
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
